import { useEffect, useState } from "react";
import { Alert, Box, CircularProgress, FormControlLabel, Snackbar, Switch, Typography } from "@mui/material";
import theme from "../styles/theme";
import { isQuizEnabled, setQuizEnabled, isCardsEnabled, setCardsEnabled, isSchemasEnabled, setSchemasEnabled } from "../api";

export default function SuperAdminQuizSettings() {
  const [quizOn, setQuizOn] = useState(true);
  const [cardsOn, setCardsOn] = useState(true);
  const [schemasOn, setSchemasOn] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState<{ open: boolean; severity: "success" | "error"; message: string }>({
    open: false,
    severity: "success",
    message: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const [quiz, cards, schemas] = await Promise.all([isQuizEnabled(), isCardsEnabled(), isSchemasEnabled()]);
        setQuizOn(quiz);
        setCardsOn(cards);
        setSchemasOn(schemas);
      } catch (e: any) {
        setSnack({ open: true, severity: "error", message: e?.message || "Erreur chargement des réglages." });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleQuizToggle = async (checked: boolean) => {
    setQuizOn(checked);
    setSaving(true);
    try {
      await setQuizEnabled(checked);
      setSnack({
        open: true,
        severity: "success",
        message: checked ? "Quiz activé pour les utilisateurs." : "Quiz désactivé pour les utilisateurs.",
      });
    } catch (e: any) {
      setQuizOn(!checked);
      setSnack({ open: true, severity: "error", message: e?.message || "Erreur lors de l'enregistrement." });
    } finally {
      setSaving(false);
    }
  };

  const handleSchemasToggle = async (checked: boolean) => {
    setSchemasOn(checked);
    setSaving(true);
    try {
      await setSchemasEnabled(checked);
      setSnack({
        open: true,
        severity: "success",
        message: checked ? "Révision des schémas activée." : "Révision des schémas désactivée (quiz seul).",
      });
    } catch (e: any) {
      setSchemasOn(!checked);
      setSnack({ open: true, severity: "error", message: e?.message || "Erreur lors de l'enregistrement." });
    } finally {
      setSaving(false);
    }
  };

  const handleCardsToggle = async (checked: boolean) => {
    setCardsOn(checked);
    setSaving(true);
    try {
      await setCardsEnabled(checked);
      setSnack({
        open: true,
        severity: "success",
        message: checked ? "Cartons activés." : "Cartons désactivés.",
      });
    } catch (e: any) {
      setCardsOn(!checked);
      setSnack({ open: true, severity: "error", message: e?.message || "Erreur lors de l'enregistrement." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3, pb: 10 }}>
      <Typography variant="h4" sx={{ mb: 1, color: theme.palette.primary.main, fontWeight: 800 }}>
        Réglages — Fonctionnalités
      </Typography>
      <Typography sx={{ mb: 3, color: theme.palette.text.secondary }}>
        Active ou désactive des fonctionnalités pour tous les utilisateurs (joueuses, coachs, staff).
      </Typography>

      <Box
        sx={{
          p: 3,
          maxWidth: 480,
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <FormControlLabel
              control={<Switch checked={quizOn} disabled={saving} onChange={(e) => handleQuizToggle(e.target.checked)} />}
              label={
                <Typography sx={{ fontWeight: 700 }}>
                  Quiz Placement {quizOn ? "activé" : "désactivé"}
                </Typography>
              }
            />
            <Typography sx={{ mt: 0.5, mb: 2.5, fontSize: 13.5, color: theme.palette.text.secondary }}>
              Quand il est désactivé, les utilisateurs voient un message indiquant que le quiz est temporairement
              indisponible.
            </Typography>

            <FormControlLabel
              control={<Switch checked={schemasOn} disabled={saving} onChange={(e) => handleSchemasToggle(e.target.checked)} />}
              label={
                <Typography sx={{ fontWeight: 700 }}>
                  Révision des schémas {schemasOn ? "activée" : "désactivée"}
                </Typography>
              }
            />
            <Typography sx={{ mt: 0.5, mb: 2.5, fontSize: 13.5, color: theme.palette.text.secondary }}>
              Quand elle est désactivée, les joueuses n'ont plus accès à la page Schémas (révision) — seul le quiz
              reste disponible. Les coachs et admins gardent l'accès au Studio pour éditer.
            </Typography>

            <FormControlLabel
              control={<Switch checked={cardsOn} disabled={saving} onChange={(e) => handleCardsToggle(e.target.checked)} />}
              label={
                <Typography sx={{ fontWeight: 700 }}>
                  Cartons {cardsOn ? "activés" : "désactivés"}
                </Typography>
              }
            />
            <Typography sx={{ mt: 0.5, fontSize: 13.5, color: theme.palette.text.secondary }}>
              Quand ils sont désactivés : la page Cartons des coachs est indisponible, l'entrée de menu est masquée
              et les joueuses ne voient plus le carton sur leur profil (les cartons déjà donnés restent en base).
            </Typography>
          </>
        )}
      </Box>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
